import { skipToken } from '@reduxjs/toolkit/query'
import { useMemo } from 'react'

import {
  PAGE_SIZE,
  useGetPickerRowsQuery,
  useGetScopedUidGroupsQuery,
} from 'api/canonicalPicker'
import type { PickerRowKind, PickerRowsPage } from 'api/canonicalPicker.types'
import type { PickerSource } from 'slices/aiConversations/pickerTypes'

import { matchesSearch } from './picksFirst'

type Args = {
  source: PickerSource
  kind: PickerRowKind
  search: string
  page: number
  scope: string[] | null
  /** The smart source that names a fixed list's products under level 1; null lists the whole catalog */
  scopeSource: PickerSource | null
  skip: boolean
}

/**
 * One page of what a level lists. The server pages a source; a fixed list
 * under level 1's picks is read whole, as no single query can scope it, and
 * searched and paged here the way the server would.
 */
const useLevelRows = ({
  source,
  kind,
  search,
  page,
  scope,
  scopeSource,
  skip,
}: Args) => {
  const isScopedList =
    kind === 'uidGroup' && scopeSource !== null && scope !== null
  const browse = useGetPickerRowsQuery(
    { source, kind, search, page, scope },
    { skip: skip || isScopedList }
  )
  const scoped = useGetScopedUidGroupsQuery(
    isScopedList && !skip && scopeSource && scope
      ? { source, scopeSource, scope }
      : skipToken
  )

  const scopedPage = useMemo((): PickerRowsPage | undefined => {
    if (!scoped.currentData) return undefined
    const matching = scoped.currentData.filter(row =>
      matchesSearch(row, search)
    )
    return {
      rows: matching.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE),
      totalCount: matching.length,
    }
  }, [page, scoped.currentData, search])

  if (!isScopedList) {
    return {
      currentData: browse.currentData,
      isFetching: browse.isFetching,
      isError: browse.isError,
      refetch: browse.refetch,
    }
  }
  return {
    currentData: scopedPage,
    isFetching: scoped.isFetching,
    isError: scoped.isError,
    refetch: scoped.refetch,
  }
}

export default useLevelRows
